import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import _isEmpty from 'lodash/isEmpty';

import { post } from 'utils';
import { UiKit, MailingList, CustomToast } from 'components';

export const RESEND_EMAIL_TYPES = {
  OS_CONFIRMATION: 'OS_CONFIRMATION',
  PAYMENT_REQUIRED: 'PAYMENT_REQUIRED'
};

export default function ResendOsEmail({
  osId = '',
  type = '',
  customerEmail = '',
  isDisabled = false,
  tooltipMessage = null,
  buttonClassName = '',
  buttonWrapperClassName = ''
}) {
  const [emails, setEmails] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { buttonText, modalTitle, modalHelptext } = getCopies(type);
  const hasNoEmailRecipients = _isEmpty(emails);

  useEffect(() => {
    if (isOpen && customerEmail) {
      setEmails(prevEmails => ({ ...prevEmails, [customerEmail]: true }));
    }
  }, [isOpen, customerEmail]);

  const onOpenModal = () => setIsOpen(true);

  const onCloseModal = () => setIsOpen(false);

  const onSendEmail = () => {
    if (!osId) return;
    setIsProcessing(true);
    const params = { routeParam: osId, type, emails: Object.keys(emails) };
    post({ apiKey: 'requests.trigger_emails' }, { params })
      .then(() => {
        const msg = 'Email sent successfully';
        CustomToast({ type: 'success', msg });
      })
      .catch(err => {
        const msg =
          'Unable to send the email at the moment. Please try again later!';
        CustomToast({ isNotified: err.notified, type: 'error', msg });
      })
      .finally(() => {
        onCloseModal();
        setIsProcessing(false);
      });
  };

  if (!RESEND_EMAIL_TYPES[type]) {
    throw new Error('Please provide a valid email type');
  }

  return (
    <>
      <UiKit.Tooltip disabled={!tooltipMessage}>
        <UiKit.Tooltip.Trigger>
          <UiKit.Button
            size="sm"
            variant="outline"
            onClick={onOpenModal}
            disabled={isDisabled}
            className={cn(buttonClassName)}
            wrapperClassName={cn(buttonWrapperClassName)}
          >
            {buttonText}
          </UiKit.Button>
        </UiKit.Tooltip.Trigger>
        <UiKit.Tooltip.Content>{tooltipMessage}</UiKit.Tooltip.Content>
      </UiKit.Tooltip>
      <UiKit.Modal isOpen={isOpen} onClose={onCloseModal} size="md">
        <UiKit.Modal.Header
          title={modalTitle}
          subTitle={modalHelptext}
          onClose={onCloseModal}
        />
        <UiKit.Modal.Body>
          <MailingList
            emails={emails}
            onUpdate={setEmails}
            disabled={isProcessing}
            label="Enter email addresses to send email to"
          />
        </UiKit.Modal.Body>
        <UiKit.Modal.Footer
          onConfirm={onSendEmail}
          confirmBtnText="Send e-mail"
          isDisabled={hasNoEmailRecipients}
          onCancel={onCloseModal}
        />
      </UiKit.Modal>
    </>
  );
}

function getCopies(type = '') {
  switch (type) {
    case RESEND_EMAIL_TYPES.OS_CONFIRMATION:
      return {
        modalTitle: 'Request confirmation email',
        modalHelptext:
          'You can re-send the return request confirmation email to your customers.',
        buttonText: 'Resend shipping email'
      };
    case RESEND_EMAIL_TYPES.PAYMENT_REQUIRED:
      return {
        modalTitle: 'Payment reminder email',
        modalHelptext:
          'You can re-send the email your customers reminding them of the payment to confirm the exchange order.',
        buttonText: 'Send payment reminder email'
      };
    default:
      return {};
  }
}
